import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AudioStreamingService } from "../../services/audioStreamingService";
import SocketService from "../../services/socketService";
import { readMobileMicToken } from "../../utils/consult/mobileMicLink";
import { useCaptureTelemetry } from "../../hooks/consult/useCaptureTelemetry";

// Ant Design Components
import { Button, Card, Badge, Divider, Space, Typography, notification } from "antd";
import { AudioOutlined, AudioMutedOutlined, CheckCircleOutlined, InfoCircleOutlined, SyncOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";

// Custom Components
import AudioWave from "../../pages/healthcare/consultation/consultationComponents/AudioWave";

const { Text, Title } = Typography;


const MobileMic: React.FC = () => {
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const roomId = queryParams.get("roomId") || "";
	// The phone's whole credential (S34). It has no session and no consultation link, so
	// without this talk refuses the handshake — the room number in the URL is a parameter, not a
	// credential, and has not been one since the QR started carrying a signed token.
	//
	// ⚠️ It arrives in the FRAGMENT, not the query string: a fragment is never sent to a server, so
	// the credential stays out of the ingress access log. Read ONCE, in a state initialiser, because
	// the effect below strips it from the address bar — re-reading it on a later render would find
	// nothing and take the page to its "missing access code" state mid-consult.
	const [micToken] = useState(() => readMobileMicToken(location.hash));

	// Core state
	const [isStreaming, setIsStreaming] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [connectionStatus, setConnectionStatus] = useState<"disconnected" | "connecting" | "connected">("disconnected");
	const [volumeLevel, setVolumeLevel] = useState(0);
	const [speakingTime, setSpeakingTime] = useState(0);
	const [isVoiceDetected, setIsVoiceDetected] = useState(false);
	const [reconnecting, setReconnecting] = useState(false);

	// Refs
	const socketServiceRef = useRef<SocketService | null>(null);
	const audioStreamingServiceRef = useRef<AudioStreamingService | null>(null);
	const audioStreamRef = useRef<MediaStream | null>(null);
	const speakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wakeLockRef = useRef<any>(null);

	useCaptureTelemetry({
		enabled: isStreaming,
		roomId,
		getSocket: () => (socketServiceRef.current?.getSocket() as any) ?? null,
		getCapture: () => audioStreamingServiceRef.current,
	});

	// Drop the credential from the address bar once it has been read into state. The fragment keeps
	// it out of the server's logs; this keeps it off the screen of a phone that is handed around a
	// consulting room, and out of anything that later shares or bookmarks the page.
	useEffect(() => {
		if (window.location.hash) {
			window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
		}
	}, []);

	// Initialize connection
	useEffect(() => {
		if (!roomId) {
			setErrorMessage("No room ID provided. Please scan a valid QR code.");
			return;
		}

		// A QR photographed or bookmarked before the token existed — or before it moved into the
		// fragment — still opens this page. Say so here rather than letting the handshake refuse
		// silently and the badge spin on "Connecting".
		if (!micToken) {
			setErrorMessage("This link is missing its access code. Ask your practitioner to reopen the Mobile Microphone window and scan the new QR code.");
			return;
		}

		setConnectionStatus("connecting");

		// Get singleton socket service instance
		socketServiceRef.current = SocketService.getInstance();

		// Initialize connection. The mic token is the credential — it replaces the practitioner
		// cookie when the QR is scanned on a browser that happens to have one.
		const socket: any = socketServiceRef.current.initialize(roomId, "inperson", { micToken });

		// Set up event listeners
		const connectHandler = () => {
			console.log("MobileMic connected via SocketService");
			setConnectionStatus("connected");
			setReconnecting(false);
			notification.success({
				message: "Connected",
				description: "Successfully connected to consultation room",
				duration: 3,
				placement: "top",
			});
		};

		const disconnectHandler = (reason: string) => {
			console.log("MobileMic disconnected:", reason);
			setConnectionStatus("disconnected");

			if (reason === "transport close" || reason === "transport error") {
				setReconnecting(true);
				notification.warning({
					message: "Connection Issue",
					description: "Attempting to reconnect...",
					duration: 3,
					placement: "top",
				});
			} else if (isStreaming) {
				stopStreaming();
				notification.error({
					message: "Disconnected",
					description: "Connection lost. Please reconnect.",
					duration: 3,
					placement: "top",
				});
			}
		};

		const errorHandler = (error: any) => {
			console.error("Socket error:", error);
			setErrorMessage(`Connection error: ${error.message || "Unknown error"}`);
		};

		// A refused handshake arrives here, not on 'error' — and the everyday reason for one is a
		// token older than its 15 minutes. Without this the badge just spins on "Connecting" while
		// socket.io retries a credential that will never be accepted again.
		const connectErrorHandler = () => {
			setErrorMessage("This QR code has expired. Ask your practitioner to reopen the Mobile Microphone window and scan the new one.");
		};

		socket.on("connect", connectHandler);
		socket.on("disconnect", disconnectHandler);
		socket.on("error", errorHandler);
		socket.on("connect_error", connectErrorHandler);

		return () => {
			// Clean up event listeners
			socket.off("connect", connectHandler);
			socket.off("disconnect", disconnectHandler);
			socket.off("error", errorHandler);
			socket.off("connect_error", connectErrorHandler);

			// Release connections
			releaseWakeLock();
			stopAudioCapture();

			if (socketServiceRef.current) {
				socketServiceRef.current.disconnect();
				socketServiceRef.current = null;
			}
		};
	}, [roomId, micToken]);

	// Start the speaking timer when voice is detected
	useEffect(() => {
		if (isVoiceDetected && isStreaming) {
			if (!speakingTimerRef.current) {
				speakingTimerRef.current = setInterval(() => {
					setSpeakingTime((prev) => prev + 1);
				}, 1000);
			}
		} else {
			if (speakingTimerRef.current) {
				clearInterval(speakingTimerRef.current);
				speakingTimerRef.current = null;
			}
		}

		return () => {
			if (speakingTimerRef.current) {
				clearInterval(speakingTimerRef.current);
			}
		};
	}, [isVoiceDetected, isStreaming]);

	// Format seconds to MM:SS
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
	};

	// Request wake lock to prevent screen from sleeping
	const requestWakeLock = async () => {
		if ("wakeLock" in navigator) {
			try {
				// @ts-ignore - WakeLock API may not be in all TypeScript definitions
				wakeLockRef.current = await navigator.wakeLock.request("screen");
				console.log("Wake Lock activated");

				// Set up wake lock release on visibility change
				document.addEventListener("visibilitychange", handleVisibilityChange);
			} catch (err) {
				console.error("Failed to activate Wake Lock:", err);
			}
		}
	};

	// Handle visibility change for wake lock
	const handleVisibilityChange = () => {
		if (document.visibilityState === "visible" && isStreaming) {
			requestWakeLock();
		}
	};

	// Release wake lock
	const releaseWakeLock = () => {
		if (wakeLockRef.current) {
			wakeLockRef.current
				.release()
				.then(() => {
					wakeLockRef.current = null;
					console.log("Wake Lock released");
				})
				.catch((err: any) => {
					console.error("Error releasing Wake Lock:", err);
				});
		}

		document.removeEventListener("visibilitychange", handleVisibilityChange);
	};

	// Stop audio capture
	const stopAudioCapture = () => {
		if (audioStreamingServiceRef.current) {
			audioStreamingServiceRef.current.stop();
			audioStreamingServiceRef.current = null;
		}

		if (audioStreamRef.current) {
			audioStreamRef.current.getTracks().forEach((track) => track.stop());
			audioStreamRef.current = null;
		}
	};

	const startStreaming = async () => {
		try {
			// Get audio stream with optimized settings
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
				},
			});
			audioStreamRef.current = stream;

			// Initialize audio streaming service
			const audioService = new AudioStreamingService({
				stream: stream,
				roomId: roomId,
				participantId: "mobile-mic",
				onVoiceDetected: (active) => {
					setIsVoiceDetected(active);
				},
				maxChunkSize: 10, // 10 seconds max per audio chunk
			});
			audioStreamingServiceRef.current = audioService;

			// A suspended context makes init() await resume() before it builds the analyser, and start() before that is refused, so the VAD never runs.
			await audioService.init();
			audioService.start();
			setIsStreaming(true);
			setSpeakingTime(0);

			notification.info({
				message: "Microphone Active",
				description: "Your phone is now streaming audio",
				duration: 3,
				placement: "top",
			});

			// Prevent screen from sleeping
			requestWakeLock();
		} catch (error) {
			console.error("Error accessing mobile microphone:", error);
			setErrorMessage("Could not access microphone. Please check your permissions.");
			notification.error({
				message: "Microphone Error",
				description: "Could not access microphone. Please check permissions.",
				duration: 5,
				placement: "top",
			});
		}
	};

	const stopStreaming = () => {
		stopAudioCapture();
		releaseWakeLock();
		setIsStreaming(false);
		setIsVoiceDetected(false);

		notification.info({
			message: "Microphone Inactive",
			description: "Audio streaming stopped",
			duration: 3,
			placement: "top",
		});
	};

	// Get badge status
	const getBadgeStatus = () => {
		if (reconnecting) return "warning";
		if (connectionStatus === "connected") return "success";
		if (connectionStatus === "connecting") return "processing";
		return "error";
	};

	const getBadgeText = () => {
		if (reconnecting) return "Reconnecting...";
		if (connectionStatus === "connected") return "Connected";
		if (connectionStatus === "connecting") return "Connecting...";
		return "Disconnected";
	};

	return (
		<div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--background-color)" }}>
			<Card style={{ maxWidth: 450, width: "100%" }} styles={{ body: { padding: 16 } }}>
				{/* Header */}
				<div className="flex items-center justify-between mb-4">
					<Space>
						<AudioOutlined style={{ fontSize: 24 }} />
						<Title level={4} style={{ margin: 0 }}>
							Mobile Microphone
						</Title>
					</Space>
					<Badge status={getBadgeStatus()} text={getBadgeText()} />
				</div>

				{/* Error Message */}
				{errorMessage && (
					<div className="mb-3 p-3 rounded" style={{ background: "rgba(255, 77, 79, 0.1)", border: "1px solid rgba(255, 77, 79, 0.3)" }}>
						<Space>
							<ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
							<Text style={{ color: "#ff4d4f", fontSize: 13 }}>{errorMessage}</Text>
						</Space>
					</div>
				)}

				{/* Reconnecting Message */}
				{reconnecting && (
					<div className="mb-3 p-3 rounded" style={{ background: "rgba(250, 173, 20, 0.1)", border: "1px solid rgba(250, 173, 20, 0.3)" }}>
						<Space>
							<SyncOutlined spin style={{ color: "#faad14" }} />
							<Text style={{ color: "#faad14", fontSize: 13 }}>Reconnecting to server...</Text>
						</Space>
					</div>
				)}

				{/* Audio Visualization */}
				<div className="mb-4">
					<Card
						size="small"
						style={{
							border: isStreaming ? "2px solid var(--primary-color)" : "1px solid var(--border-color)",
							background: "var(--background-color)",
						}}
					>
						<div className="flex justify-between items-center mb-3">
							<Text strong style={{ color: isStreaming ? "var(--primary-color)" : "var(--text-color-secondary)" }}>
								{isStreaming ? "Microphone Active" : "Microphone Inactive"}
							</Text>
							{isStreaming && (
								<Space size={4}>
									<ClockCircleOutlined style={{ color: isVoiceDetected ? "#52c41a" : "var(--text-color-secondary)", fontSize: 14 }} />
									<Text style={{ color: isVoiceDetected ? "#52c41a" : "var(--text-color-secondary)", fontSize: 13 }}>{formatTime(speakingTime)}</Text>
								</Space>
							)}
						</div>

						<div className="flex justify-center items-center p-3 rounded" style={{ height: 80, background: "rgba(0, 0, 0, 0.02)" }}>
							<AudioWave
								isActive={isStreaming}
								audioStream={audioStreamRef.current || undefined}
								visualizationStyle={"bars"}
								color={isVoiceDetected ? "#52c41a" : "var(--primary-color)"}
								width={340}
								height={60}
								barCount={32}
								onVolumeChange={(level) => setVolumeLevel(level)}
							/>
						</div>
					</Card>
				</div>

				{/* Instructions */}
				<Divider style={{ margin: "16px 0" }}>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Instructions
					</Text>
				</Divider>

				<Space direction="vertical" size={8} style={{ width: "100%" }}>
					<Space size={8}>
						<CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
						<Text style={{ fontSize: 13 }}>Keep your phone close to the conversation</Text>
					</Space>
					<Space size={8}>
						<CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
						<Text style={{ fontSize: 13 }}>Ensure your phone doesn't go to sleep</Text>
					</Space>
					<Space size={8}>
						<CheckCircleOutlined style={{ color: "#52c41a", fontSize: 14 }} />
						<Text style={{ fontSize: 13 }}>Speak clearly for best transcription quality</Text>
					</Space>
					<Space size={8}>
						<InfoCircleOutlined style={{ color: "var(--primary-color)", fontSize: 14 }} />
						<Text style={{ fontSize: 13 }} type="secondary">
							Press the mic button to start/stop streaming
						</Text>
					</Space>
				</Space>

				<Divider style={{ margin: "16px 0" }} />

				{/* Room ID & Control Button */}
				<div className="flex flex-col items-center">
					<Text type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
						Room ID: {roomId}
					</Text>
					<Button
						type={isStreaming ? "primary" : "default"}
						danger={isStreaming}
						shape="circle"
						size="large"
						icon={isStreaming ? <AudioMutedOutlined /> : <AudioOutlined />}
						onClick={isStreaming ? stopStreaming : startStreaming}
						disabled={connectionStatus !== "connected" || reconnecting}
						style={{
							width: 80,
							height: 80,
							fontSize: 32,
							boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
						}}
					/>
				</div>
			</Card>
		</div>
	);
};

export default MobileMic;
